// © Thibault Sabathier, 2025. Tous droits réservés.
// src/contexts/SocketContext.js - Version multi-tenant SÉCURISÉE (sans exposition window)
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { io } from 'socket.io-client'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getTenantSlug } from '../utils/tenant'
import { getApiUrl } from '../utils/apiClient'
import { showBrowserNotification } from '../utils/browserNotification'
import {
  addNotification,
  markNotificationRead,
  markAllNotificationsRead,
  clearNotification,
  createMessageNotification,
  setUnreadCount,
} from '../store/notifications/notificationsActions'
import {
  updateConversation,
  addConversation,
  fetchConversations,
  updateConversations,
  removeConversation,
} from '../store/conversations/conversationsActions'
import { useNotification } from './NotificationContext'
import store from '../store'

const SocketContext = createContext(null)

// Hook personnalisé pour utiliser le contexte
export const useSocketContext = () => useContext(SocketContext)

// Constantes pour des événements personnalisés existants
const USER_AUTHENTICATED_EVENT = 'user_authenticated'
const USER_LOGGED_OUT_EVENT = 'user_logged_out'

// ✅ PHASE 5.1: Constantes pour le système pub/sub centralisé
const SOCKET_NEW_MESSAGE_EVENT = 'socket:new_message'
const SOCKET_MESSAGE_DELETED_EVENT = 'socket:message_deleted'
const SOCKET_CONVERSATION_READ_EVENT = 'socket:conversation_read'
const SOCKET_NEW_CONVERSATION_EVENT = 'socket:new_conversation'
const SOCKET_USER_TYPING_EVENT = 'socket:user_typing'

// ✅ PHASE 5.1: Export des constantes pour utilisation externe
export const SOCKET_EVENTS = {
  NEW_MESSAGE: SOCKET_NEW_MESSAGE_EVENT,
  MESSAGE_DELETED: SOCKET_MESSAGE_DELETED_EVENT,
  CONVERSATION_READ: SOCKET_CONVERSATION_READ_EVENT,
  NEW_CONVERSATION: SOCKET_NEW_CONVERSATION_EVENT,
  USER_TYPING: SOCKET_USER_TYPING_EVENT,
}

// Composant principal pour gérer le socket
export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null)
  const [socketStatus, setSocketStatus] = useState({
    connected: false,
    connecting: false,
    reconnectAttempt: 0,
    reconnectError: null,
    lastError: null,
  })

  const socketRef = useRef(null)
  const { addToast } = useNotification()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Fonction pour jouer un son de notification
  const playNotificationSound = useCallback(() => {
    try {
      const audio = new Audio('/sounds/notification.mp3')
      audio.volume = 0.4
      audio.play().catch((err) => {
        console.log('Impossible de jouer le son de notification:', err)
      })
    } catch (err) {
      console.error('Erreur lors de la lecture du son:', err)
    }
  }, [])

  // Icône d'un expéditeur, absolue ou relative à l'API, avec repli.
  const senderIcon = useCallback((avatarUrl, separator = '/') => {
    if (!avatarUrl) return '/assets/avatar-placeholder.png'
    return avatarUrl.startsWith('http') ? avatarUrl : `${getApiUrl()}${separator}${avatarUrl}`
  }, [])

  // Méthode pour forcer une reconnexion
  const forceReconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
      setTimeout(() => {
        if (socketRef.current) {
          socketRef.current.connect()
        }
      }, 1000)
    }
  }, [])

  // Diagnostic périodique (log de statut)
  useEffect(() => {
    const interval = setInterval(() => {
      if (socketRef.current) {
        console.log('Statut du socket:', socketRef.current.connected ? 'Connecté' : 'Déconnecté')
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [socketRef.current])

  // [AJOUT] petite fonction interne pour tout réinitialiser
  const initSocket = useCallback(() => {
    const getState = () => store.getState()

    // ✅ SECURITY: Récupérer uniquement userId (token géré par cookie httpOnly)
    const userId = localStorage.getItem('userId')
    const redirecting = localStorage.getItem('redirecting')

    console.log('🔌 SocketContext.initSocket - Valeurs récupérées:', {
      userId,
      redirecting,
    })

    // Si une redirection est en cours, ne pas initialiser le socket
    if (redirecting === 'true') {
      console.log(
        '🔌 SocketContext.initSocket - Redirection en cours, initialisation du socket reportée',
      )
      return
    }

    // ✅ SECURITY: Vérifier uniquement userId (le cookie sera envoyé automatiquement)
    if (!userId) {
      console.log('🔌 SocketContext.initSocket - Arrêt: userId manquant')
      setSocketStatus({
        connected: false,
        connecting: false,
        reconnectAttempt: 0,
        reconnectError: null,
        lastError: null,
      })
      return
    }
    // Si un socket existait déjà, on le déconnecte
    if (socketRef.current) {
      console.log('🔌 SocketContext.initSocket - Déconnexion du socket existant')
      socketRef.current.disconnect()
      socketRef.current = null
    }

    // Création du socket
    try {
      console.log("🔌 SocketContext.initSocket - Création d'un nouveau socket")

      // Récupérer le tenant slug pour l'envoyer au backend (branding uniquement)
      const tenantSlug = getTenantSlug()
      const apiUrl = getApiUrl()
      const baseSocketUrl = apiUrl.split('/api')[0]

      // ✅ SECURITY: Socket.IO avec cookie httpOnly
      // Le cookie auth_token sera envoyé automatiquement via l'upgrade HTTP
      const socketInstance = io(baseSocketUrl, {
        auth: {
          // Plus de token ici - utiliser le cookie httpOnly
          userId: userId,
        },
        query: {
          // Ajouter le tenant en query string (optionnel, pour namespace côté backend)
          organizationSlug: tenantSlug || undefined,
        },
        withCredentials: true, // ✅ SECURITY: Envoyer le cookie httpOnly
        transports: ['websocket', 'polling'], // Fallback polling pour compatibilité cookie
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        autoConnect: true,
      })

      socketInstance.on('connect', () => {
        setSocketStatus((prev) => ({
          ...prev,
          connected: true,
          connecting: false,
          reconnectAttempt: 0,
          reconnectError: null,
        }))

        // ✅ SÉCURITÉ: Pas d'exposition window - le socket est accessible via le context
        // Rejoindre nos conversations
        socketInstance.emit('rejoin_conversations')
        // Forcer le chargement des conversations
        dispatch(fetchConversations())
        // Demander la liste des utilisateurs en ligne
        socketInstance.emit('get_online_users')
        // Émettre son propre statut
        socketInstance.emit('user_status_change', {
          userId: Number(userId),
          online: true,
        })
      })

      socketInstance.on('connect_error', (error) => {
        console.error('Erreur de connexion socket:', error)
        setSocketStatus((prev) => ({
          ...prev,
          connected: false,
          connecting: true,
          reconnectAttempt: prev.reconnectAttempt + 1,
          lastError: error.message || 'Erreur de connexion au serveur',
        }))
      })

      socketInstance.on('disconnect', (reason) => {
        console.log('Socket déconnecté:', reason)
        setSocketStatus((prev) => ({
          ...prev,
          connected: false,
          connecting: reason !== 'io server disconnect' && reason !== 'io client disconnect',
        }))
      })

      socketInstance.on('reconnect', (attemptNumber) => {
        setSocketStatus((prev) => ({
          ...prev,
          connected: true,
          connecting: false,
          reconnectAttempt: 0,
          reconnectError: null,
        }))

        socketInstance.emit('rejoin_conversations')
      })

      socketInstance.on('reconnect_error', (error) => {
        console.error('Erreur de reconnexion:', error)
        setSocketStatus((prev) => ({
          ...prev,
          reconnectError: error.message,
        }))
      })

      // Notifications
      socketInstance.on('notification', (notification) => {
        dispatch(addNotification(notification))
        playNotificationSound()
      })

      // NOUVEAU: Gestionnaire pour les notifications de vue de profil
      socketInstance.on('profile_view_notification', (data) => {
        console.log('Notification de vue de profil reçue dans SocketContext:', data)

        // Ne traiter que si c'est pour l'utilisateur connecté et pas de lui-même
        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          // Créer la notification
          const notificationData = {
            id: `profile-view-${data.senderId}-${Date.now()}`,
            type: 'profile_view',
            title: `${data.senderFirstName} ${data.senderLastName}`,
            content: 'a consulté votre profil',
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          // Ajouter au store Redux
          dispatch(addNotification(notificationData))

          // Son de notification
          playNotificationSound()

          // Notification du navigateur si l'utilisateur n'est pas sur la page.
          // `data.url` est le contrat de clic lu par public/sw-push.js.
          showBrowserNotification({
            title: `${data.senderFirstName} ${data.senderLastName}`,
            body: 'a consulté votre profil',
            icon: senderIcon(data.senderAvatarUrl, ''),
            tag: `profile-view-${data.senderId}`,
            data: { type: 'profile_view', url: `/members/${data.senderId}` },
            onClick: () => navigate(`/members/${data.senderId}`),
          })
        }
      })

      socketInstance.on('notification_read', ({ id }) => {
        dispatch(markNotificationRead(id))
      })
      socketInstance.on('all_notifications_read', () => {
        dispatch(markAllNotificationsRead())
      })
      socketInstance.on('notification_deleted', ({ id }) => {
        dispatch(clearNotification(id))
      })
      // Compteur de la cloche : ces deux événements appartiennent aux
      // NOTIFICATIONS, pas à la messagerie. Ils alimentaient autrefois un slice
      // `messages` parallèle, depuis supprimé — le compteur autoritatif du
      // serveur était donc jeté, et la cloche ne vivait que de ses incréments
      // locaux, sans jamais se resynchroniser.
      //
      // Les deux charges utiles portent un `count` autoritatif — le serveur
      // n'émet `unread_notification_count_update` qu'avec `{ count: 0,
      // action: 'reset' }` — donc `setUnreadCount` suffit dans les deux cas,
      // sans rejouer la logique increment/decrement côté client.
      socketInstance.on('unread_notification_count', ({ count }) => {
        dispatch(setUnreadCount(count))
      })
      socketInstance.on('unread_notification_count_update', ({ count }) => {
        dispatch(setUnreadCount(count))
      })
      socketInstance.on('unread_notifications', (notifications) => {
        if (Array.isArray(notifications) && notifications.length > 0) {
          notifications.forEach((notification) => {
            dispatch(addNotification(notification))
          })
          const messageNotifications = notifications.filter((n) => n.type === 'message')
          if (messageNotifications.length > 0) {
            playNotificationSound()
          }
        }
      })

      // NOUVEAU: Gestionnaire pour les notifications de messages dans les groupes de discussion
      socketInstance.on('discussion_group_message_notification', (data) => {
        console.log('Notification de message de groupe reçue dans SocketContext:', data)

        // Ne traiter que si c'est pour l'utilisateur connecté et pas de lui-même
        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          // Créer la notification
          const notificationData = {
            id: `discussion-group-message-${data.messageId}-${Date.now()}`,
            type: 'discussion_group_message',
            title: `${data.senderFirstName} ${data.senderLastName}`,
            content: `a écrit un message dans le groupe "${data.groupTitle}"`,
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            groupId: data.groupId,
            groupTitle: data.groupTitle,
            messageId: data.messageId,
            messageContent: data.messageContent,
            originalMessage: data.originalMessage,
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          // Ajouter au store Redux
          dispatch(addNotification(notificationData))

          // Son de notification
          playNotificationSound()

          // Notification du navigateur si l'utilisateur n'est pas sur la page.
          showBrowserNotification({
            title: `${data.senderFirstName} ${data.senderLastName}`,
            body: `a écrit un message dans le groupe "${data.groupTitle}"`,
            icon: senderIcon(data.senderAvatarUrl, ''),
            tag: `discussion-group-${data.groupId}`,
            data: { type: 'discussion_group', url: `/discussions/${data.groupId}` },
            onClick: () => navigate(`/discussions/${data.groupId}`),
          })
        }
      })

      // Gestionnaire pour les notifications de réaction sur un post
      socketInstance.on('post_reaction_notification', (data) => {
        console.log('Notification de réaction sur post reçue:', data)

        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          const notificationData = {
            id: `post-reaction-${data.postId}-${data.senderId}-${Date.now()}`,
            type: 'post_reaction',
            content: JSON.stringify({
              postId: data.postId,
              reactionType: data.reactionType,
              message: data.message,
            }),
            postId: data.postId,
            reactionType: data.reactionType,
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          dispatch(addNotification(notificationData))
          playNotificationSound()
        }
      })

      // Gestionnaire pour les notifications de commentaire sur un post
      socketInstance.on('post_comment_notification', (data) => {
        console.log('Notification de commentaire sur post reçue:', data)

        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          const notificationData = {
            id: `post-comment-${data.postId}-${data.commentId}-${Date.now()}`,
            type: 'post_comment',
            content: JSON.stringify({
              postId: data.postId,
              commentId: data.commentId,
              originalComment: data.originalComment,
              message: data.message,
            }),
            postId: data.postId,
            commentId: data.commentId,
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          dispatch(addNotification(notificationData))
          playNotificationSound()
        }
      })

      // Gestionnaire pour les notifications de tag sur une photo
      socketInstance.on('post_tag_notification', (data) => {
        console.log('Notification de tag sur post reçue:', data)

        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          const notificationData = {
            id: `post-tag-${data.postId}-${data.tagId}-${Date.now()}`,
            type: 'post_tag',
            content: JSON.stringify({
              postId: data.postId,
              tagId: data.tagId,
              message: data.message,
            }),
            postId: data.postId,
            tagId: data.tagId,
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          dispatch(addNotification(notificationData))
          playNotificationSound()
        }
      })

      // Gestionnaire pour les notifications de mention dans un post/commentaire
      socketInstance.on('post_mention_notification', (data) => {
        console.log('Notification de mention sur post reçue:', data)

        if (data.recipientId === Number(userId) && data.senderId !== Number(userId)) {
          const notificationData = {
            id: `post-mention-${data.postId}-${data.itemId}-${Date.now()}`,
            type: 'post_mention',
            content: JSON.stringify({
              postId: data.postId,
              itemId: data.itemId,
              mentionType: data.mentionType,
              message: data.message,
            }),
            postId: data.postId,
            itemId: data.itemId,
            mentionType: data.mentionType,
            sender: {
              id: data.senderId,
              firstName: data.senderFirstName,
              lastName: data.senderLastName,
              avatarUrl: data.senderAvatarUrl,
            },
            timestamp: data.timestamp,
            created_at: data.timestamp,
            read: false,
          }

          dispatch(addNotification(notificationData))
          playNotificationSound()
        }
      })

      // ✅ PHASE 5.1: Handler UNIQUE centralisé pour new_message
      // Les autres composants écoutent via window.addEventListener('socket:new_message')
      socketInstance.on('new_message', (msg) => {
        console.log('🔌 SocketContext: Nouveau message reçu (handler centralisé):', msg)
        if (!msg || !msg.conversation_id) {
          console.warn('Message invalide reçu:', msg)
          return
        }

        // ✅ PHASE 5.1: Dispatch CustomEvent IMMÉDIATEMENT pour les autres composants
        window.dispatchEvent(
          new CustomEvent(SOCKET_NEW_MESSAGE_EVENT, {
            detail: { message: msg, timestamp: Date.now() },
          }),
        )

        const state = getState()
        const conversations = state.conversations?.conversations || []
        const currentConversationId = state.conversations?.currentConversationId
        const messageWithTime = {
          ...msg,
          id: msg.id || `temp-${Date.now()}`,
          conversation_id: Number(msg.conversation_id),
          content: msg.content || '',
          sender_id: msg.sender_id,
          createdAt: msg.created_at || msg.createdAt || new Date().toISOString(),
        }

        const isFromCurrentUser = Number(msg.sender_id) === Number(userId)
        const isCurrentConversation = Number(currentConversationId) === Number(msg.conversation_id)
        const conversation = conversations.find((c) => Number(c.id) === Number(msg.conversation_id))

        if (!conversation) {
          // Le slice Redux `conversations` n'est lu par AUCUN composant : la
          // messagerie flottante vit sur le singleton de useFloatingMessaging,
          // qui a déjà traité ce message quelques microsecondes plus tôt.
          // Recharger ici déclenchait un GET /messaging/conversations complet
          // ET un cycle de rendu global À CHAQUE message reçu, en concurrence
          // avec la mise à jour du singleton. Observé 5 fois sur 5 en local et
          // 2 fois sur 2 dans la session Sentry qui s'est terminée par un
          // NotFoundError insertBefore, 72 ms après cette ligne.
          // On sort : rien à mettre à jour dans un store que personne n'affiche.
          return
        }

        const messageExists =
          conversation.messages &&
          conversation.messages.some((m) => Number(m.id) === Number(messageWithTime.id))

        if (!messageExists) {
          dispatch(
            updateConversation({
              id: Number(msg.conversation_id),
              lastMessage: messageWithTime,
              messages: messageWithTime,
              addMessage: true,
              incrementUnread: !isFromCurrentUser && !isCurrentConversation,
              timestamp: messageWithTime.createdAt,
            }),
          )
        }

        // 🆕 CORRECTION PRINCIPALE : Notifications et ouverture messagerie flottante
        if (!isFromCurrentUser) {
          // 1. Son de notification
          playNotificationSound()

          // 2. NOTIFICATION NAVIGATEUR — PAS DE NAVIGATION
          const sender = msg.sender || {}
          let senderName = "Quelqu'un"

          if (conversation?.members) {
            const senderMember = conversation.members.find((m) => m.id === msg.sender_id)
            if (senderMember) {
              senderName = `${senderMember.firstName || ''} ${senderMember.lastName || ''}`.trim()
            }
          }

          if (sender.firstName) {
            senderName = `${sender.firstName || ''} ${sender.lastName || ''}`.trim()
          }

          // `data` reprend le contrat du push serveur : le service worker
          // (public/sw-push.js) poste OPEN_CONVERSATION, que PushDeepLinkHandler
          // transforme en ouverture de la fenêtre flottante. `onClick` ne sert
          // qu'au repli bureau, où la notification appartient à la page.
          showBrowserNotification({
            title: `Message de ${senderName}`,
            body: msg.content?.substring(0, 100),
            icon: senderIcon(sender.avatarUrl),
            tag: `message-${msg.conversation_id}`,
            data: { type: 'message', conversationId: msg.conversation_id },
            onClick: () => {
              window.dispatchEvent(
                new CustomEvent('openFloatingConversation', {
                  detail: { conversationId: Number(msg.conversation_id) },
                }),
              )
            },
          })
        }
      })

      socketInstance.on('conversation_read', ({ conversationId, userId: readByUserId }) => {
        console.log('📖 Conversation marquée comme lue:', conversationId, 'par', readByUserId)

        // ✅ PHASE 5.1: Dispatch CustomEvent pour les autres composants
        window.dispatchEvent(
          new CustomEvent(SOCKET_CONVERSATION_READ_EVENT, {
            detail: { conversationId, userId: readByUserId, timestamp: Date.now() },
          }),
        )
      })

      socketInstance.on(
        'message_deleted',
        ({ messageId, conversationId, deletedBy, deleteForEveryone }) => {
          // ✅ PHASE 5.1: Dispatch CustomEvent pour les autres composants
          window.dispatchEvent(
            new CustomEvent(SOCKET_MESSAGE_DELETED_EVENT, {
              detail: {
                messageId,
                conversationId,
                deletedBy,
                deleteForEveryone,
                timestamp: Date.now(),
              },
            }),
          )

          // Supprimer du store Redux uniquement si deleteForEveryone
          // Pour un delete local, seul l'auteur de la suppression doit voir le message disparaître
          // (géré côté useFloatingMessaging via deletedBy === currentUserId)
          if (deleteForEveryone) {
            dispatch(
              updateConversation({
                id: conversationId,
                messages: (prev) => prev.filter((m) => m.id !== messageId),
              }),
            )
          }
        },
      )

      socketInstance.on('message_read', ({ messageIds, conversationId, recipient }) => {
        const state = getState()
        const conversations = state.conversations?.conversations || []
        const conversation = conversations.find((c) => Number(c.id) === Number(conversationId))

        if (conversation && recipient && messageIds && messageIds.length > 0) {
          dispatch(
            updateConversation({
              id: conversationId,
              messages: (prevMessages) => {
                return (prevMessages || []).map((msg) => {
                  // Si ce message fait partie des messages lus
                  if (messageIds.includes(Number(msg.id))) {
                    // Ajouter ou mettre à jour les notifications avec le recipient
                    const notifications = msg.notifications || []

                    // Vérifier si ce recipient n'est pas déjà dans les notifications
                    const existingNotification = notifications.find(
                      (n) => n.recipient && n.recipient.id === recipient.id,
                    )

                    if (!existingNotification) {
                      notifications.push({ recipient })
                    }

                    return { ...msg, notifications }
                  }
                  return msg
                })
              },
            }),
          )
        }
      })

      socketInstance.on('new_conversation', (conv) => {
        console.log('🔌 SocketContext: Nouvelle conversation reçue:', conv)
        if (!conv || !conv.id) {
          console.warn('Conversation invalide reçue:', conv)
          return
        }

        // ✅ PHASE 5.1: Dispatch CustomEvent pour les autres composants
        window.dispatchEvent(
          new CustomEvent(SOCKET_NEW_CONVERSATION_EVENT, {
            detail: { conversation: conv, timestamp: Date.now() },
          }),
        )

        const state = getState()
        const convId = Number(conv.id)
        const existingConv = state.conversations?.conversations?.find(
          (c) => Number(c.id) === convId,
        )
        if (existingConv) {
          return
        }
        const normalizedConv = {
          ...conv,
          id: convId,
          timestamp: conv.updatedAt || conv.created_at || new Date().toISOString(),
          updatedAt: conv.updatedAt || conv.created_at || new Date().toISOString(),
          unreadCount: conv.unreadCount || 0,
          messages: (conv.messages || []).map((m) => ({
            ...m,
            createdAt: m.created_at || m.createdAt || new Date().toISOString(),
          })),
        }
        dispatch(addConversation(normalizedConv))
        socketInstance.emit('join_conversation', convId)

        const isCreator = conv.members?.some((m) => m.id === userId && m.isCreator)
        if (!isCreator) {
          playNotificationSound()
          addToast(
            'Nouvelle conversation',
            'Vous avez été ajouté à une nouvelle conversation',
            'info',
          )
        }
      })

      // Groupes
      socketInstance.on('group_members_added', ({ groupId, newMemberIds, systemMessage }) => {
        if (systemMessage) {
          const messageWithTime = {
            ...systemMessage,
            createdAt: systemMessage.created_at || new Date().toISOString(),
            is_system_message: true,
          }
          dispatch(
            updateConversation({
              id: groupId,
              lastMessage: messageWithTime,
              messages: messageWithTime,
              addMessage: true,
              timestamp: messageWithTime.createdAt,
            }),
          )
        }
      })

      socketInstance.on('group_member_removed', ({ groupId, memberId, systemMessage }) => {
        if (memberId === userId) {
          dispatch(removeConversation(groupId))
          addToast('Retiré du groupe', "Vous avez été retiré d'un groupe de discussion.", 'info')
          return
        }
        if (systemMessage) {
          const messageWithTime = {
            ...systemMessage,
            createdAt: systemMessage.created_at || new Date().toISOString(),
            is_system_message: true,
          }
          dispatch(
            updateConversation({
              id: groupId,
              lastMessage: messageWithTime,
              messages: messageWithTime,
              addMessage: true,
              timestamp: messageWithTime.createdAt,
            }),
          )
        }
      })

      socketInstance.on('conversation_created', (conv) => {
        const newId = Number(conv.id)
        if (!newId) return
        const normalizedConv = {
          ...conv,
          id: newId,
          updatedAt: conv.updatedAt || new Date().toISOString(),
          timestamp: conv.updatedAt || new Date().toISOString(),
          messages: (conv.messages || []).map((m) => ({
            ...m,
            createdAt: m.created_at || m.createdAt || new Date().toISOString(),
          })),
          unreadCount: 0,
        }
        dispatch(addConversation(normalizedConv))
        socketInstance.emit('join_conversation', newId)
      })

      socketInstance.on('group_updated', ({ groupId, updatedGroup, systemMessage }) => {
        dispatch(
          updateConversation({
            id: groupId,
            ...updatedGroup,
          }),
        )
        if (systemMessage) {
          const messageWithTime = {
            ...systemMessage,
            createdAt: systemMessage.created_at || new Date().toISOString(),
            is_system_message: true,
          }
          dispatch(
            updateConversation({
              id: groupId,
              lastMessage: messageWithTime,
              messages: messageWithTime,
              addMessage: true,
              timestamp: messageWithTime.createdAt,
            }),
          )
        }
      })

      // Compte supprimé
      socketInstance.on('account_deleted', () => {
        addToast(
          'Compte supprimé',
          'Votre compte a été supprimé par un administrateur. Vous allez être déconnecté.',
          'danger',
          10000,
        )
        setTimeout(() => {
          const lang = localStorage.getItem('terrilink_lang')
          localStorage.clear()
          if (lang) localStorage.setItem('terrilink_lang', lang)
          navigate('/login', { replace: true })
          window.location.reload()
        }, 3000)
      })

      socketInstance.on('user_conversations', (conversations) => {
        dispatch(updateConversations(conversations))
      })

      // Démarrer la connexion
      if (!socketInstance.connected) {
        socketInstance.connect()
      }

      // On stocke le socket
      socketRef.current = socketInstance
      setSocket(socketInstance)

      // Fonction de nettoyage des gestionnaires d'événements
      const cleanupSocketListeners = () => {
        if (socketInstance) {
          // Nettoyer tous les gestionnaires d'événements
          socketInstance.off('connect')
          socketInstance.off('connect_error')
          socketInstance.off('disconnect')
          socketInstance.off('reconnect')
          socketInstance.off('reconnect_error')

          // Notifications
          socketInstance.off('notification')
          socketInstance.off('profile_view_notification')
          socketInstance.off('discussion_group_message_notification')
          socketInstance.off('post_reaction_notification')
          socketInstance.off('post_comment_notification')
          socketInstance.off('post_tag_notification')
          socketInstance.off('post_mention_notification')
          socketInstance.off('notification_read')
          socketInstance.off('all_notifications_read')
          socketInstance.off('notification_deleted')
          socketInstance.off('unread_notification_count')
          socketInstance.off('unread_notification_count_update')
          socketInstance.off('unread_notifications')

          // Messages
          socketInstance.off('new_message')
          socketInstance.off('message_deleted')
          socketInstance.off('message_read')
          socketInstance.off('new_conversation')

          // Groupes
          socketInstance.off('group_members_added')
          socketInstance.off('group_member_removed')
          socketInstance.off('conversation_created')
          socketInstance.off('group_updated')

          // Autres
          socketInstance.off('account_deleted')
          socketInstance.off('user_conversations')
          socketInstance.off('conversation_read')
        }
      }
      socketInstance.cleanup = cleanupSocketListeners
    } catch (error) {
      console.error('🔌 SocketContext.initSocket - Erreur:', error)
      setSocketStatus({
        connected: false,
        connecting: false,
        reconnectAttempt: 0,
        reconnectError: error.message,
        lastError: error.message,
      })
    }
  }, [dispatch, navigate, addToast, playNotificationSound, senderIcon])

  // useEffect principal: on lit userId, init le socket
  // ✅ SECURITY: Plus de token - auth gérée par cookie httpOnly
  useEffect(() => {
    const userId = localStorage.getItem('userId')

    // Si pas de userId, on arrête (le cookie sera validé côté serveur)
    if (!userId) {
      setSocketStatus({
        connected: false,
        connecting: false,
        reconnectAttempt: 0,
        reconnectError: null,
        lastError: null,
      })
      return
    }

    // Petit délai pour être sûr que tout soit en place
    const socketInitTimer = setTimeout(() => {
      initSocket()
    }, 1000)

    // Nettoyage
    return () => {
      clearTimeout(socketInitTimer)
      if (socketRef.current) {
        // Nettoyer les gestionnaires d'événements si la fonction existe
        if (socketRef.current.cleanup) {
          socketRef.current.cleanup()
        }

        // Déconnecter et nettoyer
        socketRef.current.disconnect()
        socketRef.current = null
        setSocket(null)
      }
    }
  }, [initSocket])

  // [AJOUT] On écoute l'event "auth_updated" pour réinit le socket
  useEffect(() => {
    function handleAuthUpdated() {
      console.log('SocketContext: auth_updated => on relance initSocket()')
      initSocket()
    }
    document.addEventListener('auth_updated', handleAuthUpdated)
    return () => {
      document.removeEventListener('auth_updated', handleAuthUpdated)
    }
  }, [initSocket])

  // Écouter les événements d'auth "user_authenticated" / "user_logged_out"
  useEffect(() => {
    function handleAuthEvent(event) {
      if (event.detail && event.detail.userId) {
        dispatch(fetchConversations())
      }
    }
    function handleLogoutEvent() {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
        setSocket(null)
        setSocketStatus({
          connected: false,
          connecting: false,
          reconnectAttempt: 0,
          reconnectError: null,
          lastError: null,
        })
      }
    }
    document.addEventListener(USER_AUTHENTICATED_EVENT, handleAuthEvent)
    document.addEventListener(USER_LOGGED_OUT_EVENT, handleLogoutEvent)
    return () => {
      document.removeEventListener(USER_AUTHENTICATED_EVENT, handleAuthEvent)
      document.removeEventListener(USER_LOGGED_OUT_EVENT, handleLogoutEvent)
    }
  }, [dispatch])

  const contextValue = {
    socket,
    status: socketStatus,
    forceReconnect,
    isConnected: socketStatus.connected,
    isConnecting: socketStatus.connecting,
  }

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>
}

export default SocketContext
